import * as searchesApi from '@apps-common/js-client/api/searches';
import Box from '@mui/material/Box';
import React, {useCallback, useEffect, useState} from 'react';
import {DndProvider} from 'react-dnd';
import {ErrorBoundary} from '@sentry/react';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {KEY_DOWN, KEY_LEFT, KEY_RIGHT, KEY_UP} from 'keycode-js';
import {MapAuthProvider} from '@apps-common/ui-maps/context/MapAuthProvider.jsx';
import {Zoom} from '@mui/material';
import {array, bool, func, object, string} from 'prop-types';
import {connect} from 'react-redux';
import {useGeographic as setGeographic} from 'ol/proj';
import {useAuth} from '@apps-common/auth-react';
import {useTranslation} from 'react-i18next';

import ErrorPage from './ui/error-page.jsx';
import FileDrop from './filedrop.jsx';
import LeftNav from './navbars/left/index.jsx';
import ModalsManager from './dialogs/index.jsx';
import Notifier from './notifications/notifier.jsx';
import RightNav from './navbars/right/index.jsx';
import SearchInput from './search-input/index.jsx';
import SessionStorageComponent from '../blobz/session-storage-component.js';
import ShiftContainer from './ui/shift-containers.jsx';
import Sidebars from './sidebars/index.jsx';
import Tour from './tour/index.jsx';
import UploadDialog from './dialogs/upload-dialog/index.jsx';
import Viewer from './viewer/index.jsx';
import WorkerManager from './worker-manager.js';
import pluckQueryParam from '../util/pluck-query-param.js';
import {
  DRAWERS,
  MAP_DRAW_LAYERS,
  MAP_TOOLS,
  MODALS,
  TILE_MODE,
  UI_MODES,
} from '../constants/index.js';
import {LoadingMessage} from './ui/message-container.jsx';
import {MAPBOX_ACCESS_TOKEN} from '@explorer/config.js';
import {addResultToView} from '@explorer/actions/results';
import {focusItem} from '../actions/focused-item.js';
import {getItem as getItemRequest} from '@explorer/api/items';
import {getMosaics, getSelectedMosaicName} from '@explorer/selectors/mosaics';
import {hideModal, resize, showModal} from '../actions/ui.js';
import {hydrateSearchGeometry} from '@explorer/util/granero-resolver';
import {loadSearch, searchSingleItem} from '../actions/search.js';
import {notify} from '@explorer/actions/notifications';
import {selectFilteredResultIds} from '../selectors/results.js';
import {selectItemTypes} from '../actions/item-types.js';
import {selectMosaic} from '../actions/mosaics.js';
import {setAccessPermissions} from '@explorer/actions/search-filters.js';
import {setActiveResultId} from '../actions/sidebar.js';
import {setDrawTool, setOverlayOn, setUserOverlayUrl} from '../actions/map.js';
import {showTimeline} from '@explorer/selectors/ui';
import {trackEvent} from '@explorer/analytics';

// This sets the openlayers userProjection to EPSG:4326
setGeographic();

const App = ({
  resize,
  showModal,
  setUserOverlayUrl,
  setOverlayOn,
  ...props
}) => {
  const auth = useAuth();
  const {t} = useTranslation();

  const [isDragging, setIsDragging] = useState(false);
  const [inFiles, setInFiles] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const [containerRef, setContainerRef] = useState();

  // https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  // there might be better ways, this works for now
  const containerRefChanged = useCallback(node => {
    if (node !== null) {
      setContainerRef(node);
    }
  }, []);

  const onResize = useCallback(() => {
    if (containerRef) {
      resize({
        width: containerRef.offsetWidth,
        height: containerRef.offsetHeight,
      });
    }
  }, [containerRef, resize]);

  const getItem = query => {
    const [itemType, itemId] = query.split('::');

    if (!itemType || !itemId) {
      return Promise.resolve(null);
    }

    // we intentionally don't care about bad requests here
    return getItemRequest({itemType, itemId}).catch(() => null);
  };

  //ToDo: Review this...
  const onKeyDown = useCallback(
    event => {
      const tagName = event.target.tagName.toUpperCase();
      const typing =
        (tagName === 'INPUT' || tagName === 'TEXTAREA') && event.target.value;

      // We don't want to modify selections if a modal is open
      if (!typing && !props.modal) {
        // sidebar is open
        const arrowKeys = [KEY_LEFT, KEY_RIGHT];

        if (props.isSidebarOpen) {
          arrowKeys.push(KEY_UP, KEY_DOWN);
        }

        if (arrowKeys.indexOf(event.keyCode) !== -1) {
          event.preventDefault();
          event.stopPropagation();

          const direction =
            event.keyCode === KEY_UP || event.keyCode === KEY_RIGHT ? -1 : 1;

          if (
            props.tileMode === TILE_MODE.RESULTS &&
            props.viewResults.length
          ) {
            const selectedResultIndex =
              direction === -1 ? props.viewResults.length - 1 : 0;
            const selectedResultId = props.viewResults[selectedResultIndex];
            const resultIndex = props.resultIds.indexOf(selectedResultId);

            const nextIndex = resultIndex + direction;
            if (nextIndex >= 0 && nextIndex < props.resultIds.length) {
              const nextResultId = props.resultIds[nextIndex];

              // this can go away if we just save the id
              const nextResult = props.resultsById[nextResultId];

              props.addResultToView({resultId: nextResult.id, reset: true});
              // clear out any sidebar selections to go back to the list
              props.setActiveResultId(null);
              props.focusItem(null);
            }
          } else if (
            props.tileMode === TILE_MODE.BASEMAPS &&
            props.selectedMosaicName
          ) {
            // we're dealing with mosaics
            const mosaicIndex = props.mosaics.findIndex(
              mosaic => mosaic.name === props.selectedMosaicName
            );

            if (mosaicIndex >= 0) {
              const nextMosaic = props.mosaics[mosaicIndex + direction];
              if (nextMosaic) {
                props.selectMosaic(nextMosaic.id);
                trackEvent('Mosaic changed', {
                  keyPress: true,
                  mosaic: nextMosaic.name,
                });
              }
            }
          }
        }
      }
    },
    [props]
  );

  const onDrawClick = () => {
    props.setDrawTool({
      name: MAP_TOOLS.DRAW_LOCKED_RECT,
      layer: MAP_DRAW_LAYERS.GEOMETRY,
    });
  };

  const onDragEnter = event => {
    const types = event.dataTransfer.types;
    for (let i = 0; i < types.length; i++) {
      if (types[i] === 'Files') {
        setIsDragging(true);
        break;
      }
    }
  };

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const onDrop = useCallback(
    event => {
      setIsDragging(false);
      setInFiles([event.dataTransfer.files[0]]);

      showModal(MODALS.UPLOAD);
    },
    [showModal, setIsDragging, setInFiles]
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    window.addEventListener(
      'dragover',
      function (e) {
        e.preventDefault();
      },
      false
    );
    window.addEventListener(
      'drop',
      e => {
        e.preventDefault();
        const items = e.dataTransfer.items;
        if (items.length > 0) {
          if (items[0].kind === 'string') {
            // probability that this is a URL.
            items[0].getAsString(itemString => {
              // oh, yeah, definitely a URL
              if (itemString.indexOf('http') === 0) {
                setUserOverlayUrl(itemString);
                setOverlayOn({name: 'user', on: true});
              }
            });
          }
        }
      },
      false
    );

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [onKeyDown, onResize, setUserOverlayUrl, setOverlayOn]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onResize, [containerRef]);

  useEffect(() => {
    const searchId = pluckQueryParam('search');
    if (searchId) {
      trackEvent('Loaded a saved search from link', {searchId});

      const fetchSearch = async () => {
        try {
          const search = await searchesApi.get(searchId);
          const hydratedSearch = await hydrateSearchGeometry(search);
          props.loadSearch(hydratedSearch);
        } catch (error) {
          props.notify({
            severity: 'error',
            message: t('savedSearch.loadError'),
          });
        }
      };

      fetchSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const queryItemId = pluckQueryParam('item');
    if (queryItemId) {
      const itemId = queryItemId.split(',')[0];
      getItem(itemId).then(item => {
        if (!item) {
          return;
        }
        props.searchSingleItem(item);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    props.setAccessPermissions(!auth.user.claims.is_trial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user.claims.is_trial]);

  if (props.fetchSubscriptionsLoading) {
    return (
      <LoadingMessage
        progressSize={60}
        suffix={t('appLayout.loadingUserInformationSuffix')}
      />
    );
  }
  if (props.fetchSubscriptionsError) {
    return <ErrorPage message={t('appLayout.errorUnknown')} />;
  }

  return (
    <ErrorBoundary
      fallback={errorProps => <ErrorPage sentryEventId={errorProps.eventId} />}
      showDialog
      dialogOptions={{
        user: {
          name: auth.user.claims.name,
          email: auth.user.claims.email,
        },
      }}
    >
      <SessionStorageComponent onReadyChange={setIsReady}>
        <MapAuthProvider
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          authToken={auth.user.accessToken}
        >
          <Box display="flex" flex={1}>
            <LeftNav />
            <Box
              ref={containerRefChanged}
              onDragEnter={onDragEnter}
              position="relative"
              flex={1}
              bgcolor="#2C2C2B"
            >
              {isReady && (
                <DndProvider backend={HTML5Backend}>
                  <Viewer
                    showTimeline={props.showTimeline}
                    leftDrawer={props.leftDrawer}
                    rightDrawer={props.rightDrawer}
                    onSearchHere={onDrawClick}
                    isComparing={props.mode === UI_MODES.COMPARE}
                    compareMethod={props.compareMethod}
                    showSnapshotLab={props.mode === UI_MODES.SNAPSHOT}
                  />
                  <React.Fragment>
                    <ShiftContainer
                      left={8}
                      position="absolute"
                      top={8}
                      width={props.showSearchBar ? 392 : 0}
                    >
                      <Zoom in={props.showSearchBar} timeout={300}>
                        <div>
                          <SearchInput />
                        </div>
                      </Zoom>
                    </ShiftContainer>
                    <Sidebars />
                  </React.Fragment>
                </DndProvider>
              )}
            </Box>
            <RightNav />
          </Box>

          <WorkerManager />
          <Notifier />
          <ModalsManager />
          <UploadDialog inFiles={inFiles} onClose={props.hideModal} />
          <FileDrop
            isDragging={isDragging}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          />
          <Tour />
        </MapAuthProvider>
      </SessionStorageComponent>
    </ErrorBoundary>
  );
};

App.propTypes = {
  isSidebarOpen: bool.isRequired,
  mode: string.isRequired,
  mosaics: array,
  resultIds: array,
  resultsById: object,
  selectedMosaicName: string,
  showSearchBar: bool,
  showTimeline: bool,
  snapshotMode: bool,
  viewResults: array.isRequired,
  leftDrawer: string,
  rightDrawer: string,

  // dispatch actions
  addResultToView: func,
  focusItem: func,
  hideModal: func,
  loadSearch: func,
  modal: string,
  notify: func,
  resize: func,
  searchSingleItem: func,
  selectItemTypes: func,
  selectMosaic: func,
  setActiveResultId: func,
  setDrawTool: func,
  setOverlayOn: func,
  setUserOverlayUrl: func,
  showModal: func,
  tileMode: string,
  compareMethod: string,
  fetchSubscriptionsLoading: bool,
  fetchSubscriptionsError: bool,
  setAccessPermissions: func,
};

const mapStateToProps = state => ({
  leftDrawer: state.ui.drawers.left,
  rightDrawer: state.ui.drawers.right,
  isSidebarOpen:
    state.ui.drawers.left === DRAWERS.SEARCH ||
    state.ui.drawers.left === DRAWERS.BASEMAPS,
  modal: state.ui.modal,
  mode: state.ui.mode,
  resultIds: selectFilteredResultIds(state),
  resultsById: state.results.resultsById,
  selectedMosaicName: getSelectedMosaicName(state),
  mosaics: getMosaics(state),
  snapshotMode: state.ui.snapshotMode,
  tileMode: state.tiles.mode,
  showTimeline: showTimeline(state),
  showSearchBar: state.ui.mode !== UI_MODES.SNAPSHOT,
  viewResults: state.results.viewResults,
  compareMethod: state.compare.method,
  fetchSubscriptionsLoading: state.subscriptions.loading,
  fetchSubscriptionsError: !!state.subscriptions.error,
});

const mapDispatchToProps = {
  addResultToView,
  focusItem,
  hideModal,
  loadSearch,
  notify,
  resize,
  searchSingleItem,
  selectItemTypes,
  selectMosaic,
  setActiveResultId,
  setDrawTool,
  setOverlayOn,
  setUserOverlayUrl,
  showModal,
  setAccessPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
